import { useAccount, useWriteContract, useReadContract, useWaitForTransactionReceipt } from 'wagmi'
import { getContractAddresses, formatUSDC, parseUSDC, USDC_ABI, PRESALE_MANAGER_ABI } from '@/lib/contracts'
import { useState, useEffect, useRef } from 'react'
import { ERROR_MESSAGES, formatErrorMessage, logError } from '@/lib/errorMessages'

export function usePresaleManager() {
  const { address, chainId } = useAccount()
  const { writeContract, data: hash, isPending, error } = useWriteContract()
  const receiptQuery = useWaitForTransactionReceipt({
    hash,
    chainId, // ✅ Explicitly specify chainId to ensure we check the right network
    query: {
      enabled: !!hash,
      refetchInterval: 1000, // Poll every second
    }
  })
  
  const { 
    isLoading: isConfirming, 
    isSuccess: isConfirmed,
    isError: isReceiptError,
    error: receiptError 
  } = receiptQuery
  
  console.log('📡 Receipt query state:', { 
    hash: hash?.slice(0, 10), 
    isConfirming, 
    isConfirmed, 
    isReceiptError,
    data: receiptQuery.data ? 'has data' : 'no data'
  })

  const [transactionStatus, setTransactionStatus] = useState<{
    type: 'create' | 'contribute' | 'approve' | 'graduate' | 'cancel' | null
    status: 'pending' | 'confirming' | 'success' | 'error'
    message: string
  }>({ type: null, status: 'pending', message: '' })

  const lastConfirmedHash = useRef<string | null>(null)
  const contracts = getContractAddresses(chainId)

  // Debug logging for transaction states
  useEffect(() => {
    if (hash) {
      console.log('🔄 Transaction state:', { 
        hash: hash.slice(0, 10) + '...', 
        chainId,
        isConfirming, 
        isConfirmed, 
        isReceiptError,
        status: transactionStatus.status,
        lastConfirmedHash: lastConfirmedHash.current?.slice(0, 10) 
      })
    }
  }, [hash, isConfirming, isConfirmed, isReceiptError, transactionStatus.status, chainId])

  // Create presale
  const createPresale = async (question: string, targetAmount: string, deadline: string) => {
    try {
      setTransactionStatus({ type: 'create', status: 'pending', message: 'Creating presale...' })
      
      const deadlineTimestamp = Math.floor(new Date(deadline).getTime() / 1000)
      const targetAmountWei = formatUSDC(targetAmount)
      // Minimum Reality.eth bond (0.00001 ETH / 10000 gwei) - minimal cost
      const minBondWei = BigInt('10000000000000') // 0.00001 ETH

      console.log('📝 Creating presale:', { question, targetAmount, deadline: new Date(deadlineTimestamp * 1000).toISOString() })

      // @ts-expect-error - wagmi v2 type issue
      const result = await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'createPresale',
        args: [question, targetAmountWei, deadlineTimestamp, minBondWei],
      })

      console.log('📤 Transaction submitted, waiting for confirmation...')
      setTransactionStatus({ type: 'create', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.createPresale', err, { question, targetAmount, deadline })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.CREATION_FAILED)
      setTransactionStatus({ 
        type: 'create', 
        status: 'error', 
        message: errorMessage
      })
      throw err
    }
  }

  // Contribute to presale
  const contributeToPresale = async (presaleId: string, amount: string) => {
    try {
      setTransactionStatus({ type: 'contribute', status: 'pending', message: 'Contributing to presale...' })
      
      const amountWei = formatUSDC(amount)

      console.log('💰 Contributing to presale:', {
        presaleId,
        amount,
        amountWei: amountWei.toString(),
        contract: contracts.PRESALE_MANAGER,
      })

      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'contributeToPresale',
        args: [presaleId as `0x${string}`, amountWei],
      })

      console.log('✅ Contribution transaction sent')
      setTransactionStatus({ type: 'contribute', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.contributeToPresale', err, { presaleId, amount })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.CONTRIBUTION_FAILED)
      setTransactionStatus({ 
        type: 'contribute', 
        status: 'error', 
        message: errorMessage
      })
    }
  }

  // Approve USDC spending (approves unlimited amount for one-time approval)
  const approveUSDC = async (amount?: string) => {
    try {
      setTransactionStatus({ type: 'approve', status: 'pending', message: 'Approving USDC spending...' })
      
      // Approve max uint256 for unlimited spending (one-time approval)
      const maxAmount = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')

      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.USDC as `0x${string}`,
        abi: USDC_ABI,
        functionName: 'approve',
        args: [contracts.PRESALE_MANAGER as `0x${string}`, maxAmount],
      })

      setTransactionStatus({ type: 'approve', status: 'confirming', message: 'Confirming approval...' })
    } catch (err) {
      logError('usePresaleManager.approveUSDC', err, { amount })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.CONTRACT.APPROVAL_FAILED)
      setTransactionStatus({ 
        type: 'approve', 
        status: 'error', 
        message: errorMessage
      })
      throw err
    }
  }

  // Check USDC allowance
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: contracts.USDC as `0x${string}`,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: address ? [address, contracts.PRESALE_MANAGER as `0x${string}`] : undefined,
  })

  // Check USDC balance
  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: contracts.USDC as `0x${string}`,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
  })

  // Get presale details
  const getPresale = (presaleId: string) => {
    return useReadContract({
      address: contracts.PRESALE_MANAGER as `0x${string}`,
      abi: PRESALE_MANAGER_ABI,
      functionName: 'getPresale',
      args: [presaleId as `0x${string}`],
    })
  }

  // Graduate presale (owner only)
  const graduatePresale = async (presaleId: string, yesOdds: number, noOdds: number) => {
    try {
      setTransactionStatus({ type: 'graduate', status: 'pending', message: 'Graduating presale...' })
      
      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'graduatePresale',
        args: [presaleId as `0x${string}`, BigInt(yesOdds), BigInt(noOdds)],
      })

      setTransactionStatus({ type: 'graduate', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.graduatePresale', err, { presaleId, yesOdds, noOdds })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.GRADUATION_FAILED)
      setTransactionStatus({ 
        type: 'graduate', 
        status: 'error', 
        message: errorMessage
      })
    }
  }

  // Cancel presale (owner only)
  const cancelPresale = async (presaleId: string) => {
    try {
      setTransactionStatus({ type: 'cancel', status: 'pending', message: 'Cancelling presale...' })
      
      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'cancelPresale',
        args: [presaleId as `0x${string}`],
      })

      setTransactionStatus({ type: 'cancel', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.cancelPresale', err, { presaleId })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.CANCELLATION_FAILED)
      setTransactionStatus({ 
        type: 'cancel', 
        status: 'error', 
        message: errorMessage
      })
    }
  }

  // Resolve presale via Reality.eth
  const resolvePresale = async (presaleId: string) => {
    try {
      setTransactionStatus({ type: 'cancel', status: 'pending', message: 'Resolving presale...' })
      
      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'resolvePresale',
        args: [presaleId as `0x${string}`],
      })

      setTransactionStatus({ type: 'cancel', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.resolvePresale', err, { presaleId })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.RESOLUTION_FAILED)
      setTransactionStatus({ 
        type: 'cancel', 
        status: 'error', 
        message: errorMessage
      })
    }
  }

  // Force resolve presale (owner only)
  const forceResolvePresale = async (presaleId: string, answer: number) => {
    try {
      setTransactionStatus({ type: 'cancel', status: 'pending', message: 'Force resolving presale...' })
      
      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'forceResolvePresale',
        args: [presaleId as `0x${string}`, BigInt(answer)],
      })

      setTransactionStatus({ type: 'cancel', status: 'confirming', message: 'Confirming transaction...' })
    } catch (err) {
      logError('usePresaleManager.forceResolvePresale', err, { presaleId, answer })
      const errorMessage = formatErrorMessage(err, ERROR_MESSAGES.PRESALE.RESOLUTION_FAILED)
      setTransactionStatus({ 
        type: 'cancel', 
        status: 'error', 
        message: errorMessage
      })
    }
  }

  // Get all presales
  const { data: allPresales } = useReadContract({
    address: contracts.PRESALE_MANAGER as `0x${string}`,
    abi: PRESALE_MANAGER_ABI,
    functionName: 'getAllPresales',
  })

  // Get pending presales
  const { data: pendingPresales } = useReadContract({
    address: contracts.PRESALE_MANAGER as `0x${string}`,
    abi: PRESALE_MANAGER_ABI,
    functionName: 'getPendingPresales',
  })

  // Manual polling fallback - check transaction every 2 seconds
  useEffect(() => {
    if (!hash || !window.ethereum) return
    
    let pollInterval: NodeJS.Timeout
    let pollCount = 0
    const maxPolls = 30 // Stop after 60 seconds (30 * 2s)
    
    const checkTransaction = async () => {
      try {
        pollCount++
        console.log(`🔍 Manual poll #${pollCount}: Checking transaction ${hash.slice(0, 10)}...`)
        
        const receipt = await window.ethereum.request({
          method: 'eth_getTransactionReceipt',
          params: [hash],
        })
        
        console.log('📝 Manual receipt check result:', receipt ? 'found' : 'pending', receipt)
        
        if (receipt && receipt.status && hash !== lastConfirmedHash.current) {
          console.log('✅ MANUAL CHECK: Transaction confirmed!')
          lastConfirmedHash.current = hash
          clearInterval(pollInterval)
          
          setTransactionStatus({
            type: transactionStatus.type,
            status: 'success',
            message: 'Transaction confirmed!'
          })
          
          // Refetch data
          if (transactionStatus.type === 'approve') {
            setTimeout(() => refetchAllowance(), 1000)
          }
          if (transactionStatus.type === 'contribute' || transactionStatus.type === 'create') {
            setTimeout(() => refetchBalance(), 1000)
          }
        }
        
        if (pollCount >= maxPolls) {
          console.warn('⚠️ Manual polling timeout reached')
          clearInterval(pollInterval)
        }
      } catch (err) {
        console.error('❌ Manual receipt check error:', err)
      }
    }
    
    // Only start manual polling if status is confirming
    if (transactionStatus.status === 'confirming') {
      pollInterval = setInterval(checkTransaction, 2000)
      checkTransaction() // Check immediately
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [hash, transactionStatus.status, transactionStatus.type, refetchAllowance, refetchBalance])
  
  // Update transaction status based on confirmation (Wagmi hook)
  useEffect(() => {
    // Only process if we have a confirmed transaction that hasn't been handled yet
    if (isConfirmed && hash && hash !== lastConfirmedHash.current) {
      console.log('✅ WAGMI: Transaction confirmed on chain:', chainId, 'hash:', hash)
      console.log('📊 Current transaction status:', transactionStatus)
      lastConfirmedHash.current = hash
      
      // Always update to success when confirmed
      console.log('🎉 Setting status to success...')
      setTransactionStatus({
        type: transactionStatus.type,
        status: 'success',
        message: 'Transaction confirmed!'
      })
      
      // If it was an approval, refetch allowance
      if (transactionStatus.type === 'approve') {
        setTimeout(() => refetchAllowance(), 1000)
      }
      
      // If it was a contribution or creation, refetch balance
      if (transactionStatus.type === 'contribute' || transactionStatus.type === 'create') {
        setTimeout(() => refetchBalance(), 1000)
      }
    }
  }, [isConfirmed, hash, transactionStatus, refetchAllowance, refetchBalance, chainId])

  // Handle transaction receipt errors (reverted transactions)
  useEffect(() => {
    if (isReceiptError && receiptError && transactionStatus.status === 'confirming') {
      console.error('❌ Transaction reverted:', receiptError)
      logError('usePresaleManager.receiptError', receiptError, { chainId, hash, transactionStatus })
      const errorMessage = formatErrorMessage(receiptError, ERROR_MESSAGES.CONTRACT.CALL_FAILED)
      setTransactionStatus(prev => ({ 
        ...prev, 
        status: 'error', 
        message: `Transaction failed: ${errorMessage}`
      }))
    }
  }, [isReceiptError, receiptError, transactionStatus.status, hash, chainId])

  // Handle write contract errors (before transaction is sent)
  useEffect(() => {
    if (error && transactionStatus.status !== 'error') {
      logError('usePresaleManager.transactionError', error, { chainId, transactionStatus })
      const errorMessage = formatErrorMessage(error, ERROR_MESSAGES.CONTRACT.CALL_FAILED)
      setTransactionStatus(prev => ({ ...prev, status: 'error', message: errorMessage }))
    }
  }, [error, transactionStatus.status])

  /**
   * Redeem LP shares for USDC after presale is resolved
   */
  const redeemPresale = async (presaleId: string) => {
    try {
      // @ts-expect-error - wagmi v2 type issue
      writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'redeemPresale',
        args: [presaleId as `0x${string}`],
      })
      
      console.log('🎁 Redeem transaction initiated')
      return hash
    } catch (error: any) {
      logError('usePresaleManager.redeemPresale', error, { presaleId })
      const errorMessage = formatErrorMessage(error, ERROR_MESSAGES.PRESALE.REDEEM_FAILED)
      throw new Error(errorMessage)
    }
  }

  /**
   * Claim accumulated trading fees for a presale
   */
  const claimFees = async (presaleId: string) => {
    try {
      setTransactionStatus({ type: 'contribute', status: 'pending', message: 'Claiming fees...' })
      
      console.log('💰 Claiming fees for presale:', presaleId)

      // @ts-expect-error - wagmi v2 type issue
      await writeContract({
        address: contracts.PRESALE_MANAGER as `0x${string}`,
        abi: PRESALE_MANAGER_ABI,
        functionName: 'claimFees',
        args: [presaleId as `0x${string}`],
      })

      console.log('✅ Claim fees transaction sent')
      setTransactionStatus({ type: 'contribute', status: 'confirming', message: 'Confirming transaction...' })
      return hash
    } catch (error: any) {
      logError('usePresaleManager.claimFees', error, { presaleId })
      const errorMessage = formatErrorMessage(error, ERROR_MESSAGES.PRESALE.CLAIM_FEES_FAILED)
      setTransactionStatus({ 
        type: 'contribute', 
        status: 'error', 
        message: errorMessage
      })
      throw new Error(errorMessage)
    }
  }

  return {
    // Actions
    createPresale,
    contributeToPresale,
    approveUSDC,
    graduatePresale,
    cancelPresale,
    resolvePresale,
    forceResolvePresale,
    redeemPresale,
    claimFees,
    
    // Data
    allowance: allowance ? parseUSDC(allowance) : 0,
    balance: balance ? parseUSDC(balance) : 0,
    getPresale,
    allPresales,
    pendingPresales,
    refetchBalance,
    refetchAllowance,
    
    // Transaction state
    isPending,
    isConfirming,
    isConfirmed,
    hash,
    error,
    transactionStatus,
    
    // Contract addresses
    contracts,
  }
}

